import React, { useState, useEffect, useMemo } from "react";
import AdminTable from "../../Common/AdminTable";
import QuestionEditorModal from "./QuestionEditorModal";
import BulkUploadModal from "./BulkUploadModal";
import AdminDrawer from "../../Common/AdminDrawer";
import {
  getQuestionsList,
  createQuestion,
  updateQuestion,
  bulkUploadQuestions,
} from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { Plus, Upload, History, Edit2, Filter, CheckCircle2 } from "lucide-react";

const ALL_SUBJECTS = [
  "All",
  "Polity",
  "History",
  "Geography",
  "Economy",
  "Environment",
  "Science & Tech",
  "Ethics",
];

const ALL_DIFFICULTIES = ["All", "Easy", "Medium", "Hard"];

export default function QuestionBankPage() {
  const { adminUser } = useAdminAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All");
  const [selectedPyqYear, setSelectedPyqYear] = useState("All");

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [historyDrawerQuestion, setHistoryDrawerQuestion] = useState(null);
  const [toastMessage, setToastMessage] = useState("");

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const data = await getQuestionsList();
      setQuestions(data);
    } catch (err) {
      console.error("Failed to load questions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  // Derive unique PYQ years for filter
  const pyqYears = useMemo(() => {
    const years = new Set();
    questions.forEach((q) => {
      if (q.pyq_year) years.add(String(q.pyq_year));
    });
    return ["All", ...Array.from(years).sort((a, b) => b - a)];
  }, [questions]);

  // Filtered dataset
  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      if (selectedSubject !== "All" && q.subject !== selectedSubject) return false;
      if (selectedDifficulty !== "All" && q.difficulty !== selectedDifficulty) return false;
      if (selectedPyqYear !== "All" && String(q.pyq_year) !== selectedPyqYear) return false;
      if (searchQuery.trim()) {
        const s = searchQuery.toLowerCase();
        return (
          q.question.toLowerCase().includes(s) ||
          q.id.toLowerCase().includes(s) ||
          (q.topic && q.topic.toLowerCase().includes(s))
        );
      }
      return true;
    });
  }, [questions, selectedSubject, selectedDifficulty, selectedPyqYear, searchQuery]);

  const handleSaveQuestion = async (formData) => {
    try {
      if (editingQuestion) {
        await updateQuestion(
          editingQuestion.id,
          formData,
          adminUser.email,
          formData.change_reason || "Question content corrected"
        );
        showToast(`Question ${editingQuestion.id} updated (v${(editingQuestion.version || 1) + 1})`);
      } else {
        const created = await createQuestion(formData, adminUser.email);
        showToast(`Question ${created.id} created successfully!`);
      }
      await loadQuestions();
    } catch (err) {
      console.error(err);
      alert("Failed to save question: " + err.message);
    }
  };

  const handleBulkUploadSuccess = async (validQuestions) => {
    try {
      const res = await bulkUploadQuestions(validQuestions, adminUser.email);
      showToast(`Successfully uploaded ${res.count} questions into Question Bank!`);
      await loadQuestions();
    } catch (err) {
      console.error(err);
      alert("Bulk upload failed: " + err.message);
    }
  };

  const columns = [
    {
      title: "ID / Version",
      key: "id",
      width: "120px",
      render: (val, row) => (
        <div>
          <span style={{ fontWeight: 700, color: "#0284c7" }}>{val}</span>
          <span
            style={{
              marginLeft: "6px",
              fontSize: "10px",
              padding: "1px 5px",
              background: "#e0f2fe",
              color: "#0369a1",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            v{row.version || 1}
          </span>
        </div>
      ),
    },
    {
      title: "Subject & Topic",
      key: "subject",
      width: "180px",
      render: (val, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>{row.topic}</div>
        </div>
      ),
    },
    {
      title: "Question Statement",
      key: "question",
      render: (val) => (
        <div
          style={{
            maxWidth: "420px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "#334155",
          }}
          title={val}
        >
          {val}
        </div>
      ),
    },
    {
      title: "Key",
      key: "correct_answer",
      width: "70px",
      align: "center",
      render: (val) => (
        <span
          style={{
            display: "inline-block",
            width: "22px",
            height: "22px",
            lineHeight: "22px",
            textAlign: "center",
            borderRadius: "50%",
            background: "#dcfce7",
            color: "#16a34a",
            fontWeight: 700,
            fontSize: "12px",
          }}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Difficulty",
      key: "difficulty",
      width: "100px",
      render: (val) => {
        const cls = (val || "").toLowerCase();
        return <span className={`status-badge ${cls}`}>{val}</span>;
      },
    },
    {
      title: "PYQ",
      key: "pyq_year",
      width: "80px",
      render: (val) => (val ? <span style={{ fontWeight: 600, color: "#475569" }}>{val}</span> : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: "120px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => {
              setEditingQuestion(row);
              setIsEditorOpen(true);
            }}
            title="Edit question & create new version"
          >
            <Edit2 size={13} />
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => setHistoryDrawerQuestion(row)}
            title="View question audit & version history"
          >
            <History size={13} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Toast notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      {/* Filter Header Toolbar */}
      <div
        className="admin-card"
        style={{ marginBottom: "16px", padding: "14px 20px" }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={16} style={{ color: "#64748b" }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Filters:</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Subject:</span>
            <select
              className="admin-form-select"
              style={{ width: "140px", padding: "4px 8px", fontSize: "13px" }}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              {ALL_SUBJECTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Difficulty:</span>
            <select
              className="admin-form-select"
              style={{ width: "120px", padding: "4px 8px", fontSize: "13px" }}
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
            >
              {ALL_DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>PYQ Year:</span>
            <select
              className="admin-form-select"
              style={{ width: "110px", padding: "4px 8px", fontSize: "13px" }}
              value={selectedPyqYear}
              onChange={(e) => setSelectedPyqYear(e.target.value)}
            >
              {pyqYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: "10px" }}>
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setIsBulkOpen(true)}
            >
              <Upload size={15} /> Bulk Upload (CSV/Excel)
            </button>
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => {
                setEditingQuestion(null);
                setIsEditorOpen(true);
              }}
            >
              <Plus size={15} /> Add Question
            </button>
          </div>
        </div>
      </div>

      {/* Main Questions Table */}
      <AdminTable
        columns={columns}
        data={filteredQuestions}
        searchPlaceholder="Search questions by text, ID, topic..."
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        pageSize={10}
        rowKey="id"
      />

      {/* Question Editor Modal */}
      <QuestionEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        initialData={editingQuestion}
        onSave={handleSaveQuestion}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        onUploadSuccess={handleBulkUploadSuccess}
      />

      {/* Version History Drawer */}
      <AdminDrawer
        isOpen={Boolean(historyDrawerQuestion)}
        onClose={() => setHistoryDrawerQuestion(null)}
        title={
          historyDrawerQuestion
            ? `Version & Audit History: ${historyDrawerQuestion.id}`
            : ""
        }
        width="600px"
      >
        {historyDrawerQuestion && (
          <div>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--admin-border)",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "4px" }}>
                {historyDrawerQuestion.subject} &bull; {historyDrawerQuestion.topic}
              </div>
              <p style={{ fontSize: "13px", color: "#475569", margin: 0, fontStyle: "italic" }}>
                "{historyDrawerQuestion.question}"
              </p>
            </div>

            <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "14px" }}>
              Version Changelog
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(historyDrawerQuestion.history || []).map((h, i) => (
                <div
                  key={i}
                  style={{
                    border: "1px solid var(--admin-border)",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    background: i === 0 ? "#f0fdf4" : "#ffffff",
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "13px",
                        color: i === 0 ? "#16a34a" : "#0284c7",
                      }}
                    >
                      Version {h.version} {i === 0 ? "(Current)" : ""}
                    </span>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                  </div>

                  <div style={{ fontSize: "13px", color: "#334155", marginBottom: "6px" }}>
                    {h.changes}
                  </div>

                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                    Edited by: <strong>{h.editedBy}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </AdminDrawer>
    </div>
  );
}
